/**
 * OrderByItemRow — single ORDER BY entry row (expression + direction
 * TogglePill + optional NULLS mini-dropdown + × button). The direction
 * control and the NULLS dropdown become live when the parent supplies an
 * `editContext` AND the resolver service has a schema snapshot. Direction
 * cycling + NULLS selection both commit via the `modifyOrderBy` chain
 * (SR-Edit lacks a `modify-order-by` op; the helper composes remove/add); ×
 * commits a `removeOrderBy`.
 *
 * Direction is a `TogglePill` (Wave 0 primitive #7) — keeps the ⇅ glyph, gains
 * the shared hover-ring affordance + `aria-pressed`. NULLS is a labeled
 * mini-dropdown (`OrderByNullsDropdown`) — `NULLS: default` / `FIRST` /
 * `LAST` — replacing the old blind 3-state cycle + cryptic em-dash (SEL-13).
 *
 * NULLS dialect gating is the parent's responsibility (`showNullsToggle`).
 *
 * PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — add/remove ORDER BY.
 */

import { ArrowUpDown } from 'lucide-react';

import type { VqeEditContext } from '../../edit/editContext';
import { extractStructureForContext, toCommitCtx } from '../../edit/editContext';
import {
  formatOrderByClauseText,
  type OrderByDirection,
  type OrderByNulls,
} from '../../edit/edits/addOrderBy';
import { modifyOrderBy } from '../../edit/edits/modifyOrderBy';
import { removeOrderBy } from '../../edit/edits/removeOrderBy';
import { vqeCopy } from '../../ux/vqeCopy';
import { RemoveButton } from '../common/RemoveButton';
import { TogglePill } from '../common/TogglePill';
import { VqeTooltip } from '../common/VqeTooltip';
import { OrderByNullsDropdown } from './OrderByNullsDropdown';
import type { ParsedOrderItem } from './parseSelectItem';

const DIRECTION_NOT_READY_TOOLTIP =
  'Toggle sort direction · editing isn’t ready · connect and wait for the schema, or open “Edit as SQL”';

/**
 * `TogglePill`'s `disabled`/`disabledTooltip` pair is a discriminated union
 * (a disabled pill can't omit its reason) — a plain `boolean` like `editReady`
 * doesn't narrow to either union member on its own, so this builds the
 * matching literal-typed props object instead of passing `disabled={!editReady}`
 * directly. No behavior change: the reason was already supplied unconditionally.
 */
function directionDisabledProps(
  editReady: boolean,
): { readonly disabled?: false } | { readonly disabled: true; readonly disabledTooltip: string } {
  return editReady
    ? { disabled: false }
    : { disabled: true, disabledTooltip: DIRECTION_NOT_READY_TOOLTIP };
}

export interface OrderByItemRowProps {
  readonly item: ParsedOrderItem;
  readonly index: number;
  readonly showNullsToggle: boolean;
  readonly editContext?: VqeEditContext;
  /** Available iff `editContext` is supplied AND the service has a snapshot. */
  readonly editReady: boolean;
  /** Statement startOffset, required by `modifyOrderBy` to refresh statement id between commits. */
  readonly statementStartOffset: number;
  /** True when the entry references a table not in the query's FROM/JOIN. */
  readonly outOfScope?: boolean;
}

export function OrderByItemRow({
  item,
  index,
  showNullsToggle,
  editContext,
  editReady,
  statementStartOffset,
  outOfScope = false,
}: OrderByItemRowProps) {
  const direction = item.direction; // 'ASC' | 'DESC' | null
  const nulls = item.nulls; // 'FIRST' | 'LAST' | null

  const handleDirectionToggle = (): void => {
    if (!editReady || !editContext) return;
    const next = nextDirection(direction);
    void commitChange(editContext, index, item.expression, next, toRenderedNulls(nulls), statementStartOffset);
  };

  const handleRemove = (): void => {
    if (!editReady || !editContext) return;
    void removeOrderBy(index, toCommitCtx(editContext));
  };

  return (
    <li
      className={
        outOfScope
          ? 'flex items-center gap-2 rounded-md border border-red-500/60 bg-red-500/10 px-2 py-1'
          : 'flex items-center gap-2 rounded-md border border-border bg-muted/30 px-2 py-1'
      }
      data-testid={`vqe-order-by-item-${index}`}
      data-out-of-scope={outOfScope ? 'true' : 'false'}
    >
      <span
        className={
          outOfScope
            ? 'flex-1 truncate font-mono text-xs text-red-600 dark:text-red-400'
            : 'flex-1 truncate font-mono text-xs'
        }
        data-testid={`vqe-order-by-item-expr-${index}`}
      >
        {item.expression}
      </span>
      {outOfScope ? (
        <VqeTooltip
          selfProvider
          content="This column references a table that is not in FROM/JOIN. Add the table or remove the entry."
        >
          <span
            className="shrink-0 cursor-help rounded bg-red-500/15 px-1 py-0 text-[9px] font-semibold uppercase tracking-wide text-red-600 dark:text-red-400"
            data-testid={`vqe-order-by-out-of-scope-${index}`}
          >
            unknown table
          </span>
        </VqeTooltip>
      ) : null}
      <TogglePill
        testId={`vqe-order-by-item-direction-${index}`}
        ariaLabel={`Toggle sort direction (currently ${direction ?? 'ASC'})`}
        pressed={direction === 'DESC'}
        icon={<ArrowUpDown className="h-3 w-3" aria-hidden />}
        tooltip="Click to cycle ASC → DESC"
        onClick={handleDirectionToggle}
        {...directionDisabledProps(editReady)}
      >
        {direction ?? 'ASC'}
      </TogglePill>
      {showNullsToggle ? (
        <OrderByNullsDropdown
          index={index}
          nulls={nulls}
          expression={item.expression}
          renderedDirection={toRenderedDirection(direction)}
          editReady={editReady}
          editContext={editContext}
          statementStartOffset={statementStartOffset}
        />
      ) : null}
      <RemoveButton
        testId={`vqe-order-by-remove-${index}`}
        ariaLabel="Remove sort"
        disabled={!editReady}
        tooltip={vqeCopy.disabled.removeSortNotReady}
        onClick={editReady ? handleRemove : undefined}
      />
    </li>
  );
}

function toRenderedDirection(d: 'ASC' | 'DESC' | null): OrderByDirection {
  if (d === 'ASC') return 'asc';
  if (d === 'DESC') return 'desc';
  return null;
}

function toRenderedNulls(n: 'FIRST' | 'LAST' | null): OrderByNulls {
  if (n === 'FIRST') return 'first';
  if (n === 'LAST') return 'last';
  return null;
}

function nextDirection(current: 'ASC' | 'DESC' | null): OrderByDirection {
  // Cycle: null/ASC → DESC → ASC → DESC … (we drop "no direction" since SQL
  // ASC is already the default; cycling stays explicit ASC ↔ DESC).
  if (current === 'DESC') return 'asc';
  return 'desc';
}

async function commitChange(
  editContext: VqeEditContext,
  index: number,
  expression: string,
  direction: OrderByDirection,
  nulls: OrderByNulls,
  statementStartOffset: number,
): Promise<void> {
  const structure = extractStructureForContext(editContext);
  const newClauseText = formatOrderByClauseText(expression, direction, nulls);
  await modifyOrderBy(index, newClauseText, toCommitCtx(editContext), {
    structure,
    statementStartOffset,
  });
}
